import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFilteredBrandList,
  selectPaginationSearchBrand,
  selectSearchLoading,
} from 'redux/selectors';
import {
  searchBrandByLimit,
  setCurrentBrand,
  setCurrentLocation,
} from 'redux/actions/mev3/meV3';

const useBrandList = (selectedId, selectedBrand, brandData, open, id) => {
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const [isSearch, setIsSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const searchLoading = useSelector(selectSearchLoading);
  const filteredBrandList = useSelector(selectFilteredBrandList);
  const paginationSearchBrand = useSelector(selectPaginationSearchBrand);

  // Auto-select first brand when search results arrive, or clear if no results
  useEffect(() => {
    if (isSearch && searchValue.trim() && !searchLoading) {
      if (filteredBrandList?.length > 0) {
        // Auto-select first brand if search results exist
        dispatch(setCurrentBrand(filteredBrandList[0]));
      } else {
        // Clear current brand and location if no search results
        dispatch(setCurrentBrand(null));
        dispatch(setCurrentLocation(null));
      }
    } else if (!isSearch && !searchValue.trim() && brandData?.length > 0) {
      // When search is cleared, revert to default selection
      dispatch(setCurrentBrand(brandData[0]));
    }
  }, [
    isSearch,
    searchValue,
    searchLoading,
    filteredBrandList,
    brandData,
    dispatch,
  ]);

  // Don't reset search when dropdown opens - let user maintain their search
  // useEffect(() => {
  //   if (open && id === 'simple-popover') {
  //     setIsSearch(false);
  //     setSearchValue('');
  //   }
  // }, [open, id]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      dispatch({
        type: 'RESET_SEARCH_BRAND_LIST',
        payload: null,
      });
      setIsSearch(false);
      setSearchValue('');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Optimized debounced search function
  const debouncedSearch = useCallback(
    (sanitizedValue) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (sanitizedValue) {
        setIsSearch(true);
        // Don't clear previous search results immediately - let them show during loading
        const isActive =
          localStorage.getItem('showActiveBusiness') === null
            ? true
            : Boolean(Number(localStorage.getItem('showActiveBusiness')));
        timeoutRef.current = setTimeout(() => {
          dispatch(searchBrandByLimit(10, 1, sanitizedValue, false, isActive));
        }, 300);
      } else {
        setIsSearch(false);
        // Clear search results when clearing search input
        dispatch({
          type: 'RESET_SEARCH_BRAND_LIST',
          payload: null,
        });
      }
    },
    [dispatch]
  );

  // Optimized fetch more data for search results
  const fetchMoreDataBySearch = useCallback(() => {
    if (searchValue.trim() && paginationSearchBrand?.hasNextPage) {
      const isActive =
        localStorage.getItem('showActiveBusiness') === null
          ? true
          : Boolean(Number(localStorage.getItem('showActiveBusiness')));
      dispatch(
        searchBrandByLimit(
          10,
          paginationSearchBrand.nextPage,
          searchValue.trim(),
          true,
          isActive
        )
      );
    }
  }, [
    dispatch,
    searchValue,
    paginationSearchBrand?.hasNextPage,
    paginationSearchBrand?.nextPage,
  ]);

  // Memoized active brand checker
  const activeBrand = useCallback(
    (item) => {
      return selectedId === item?._id ||
        item?._id === selectedBrand?.value?._id ||
        item?._id === selectedBrand?._id
        ? 'active-brand'
        : '';
    },
    [selectedId, selectedBrand?.value?._id, selectedBrand?._id]
  );

  // Memoized function to generate styles for each item
  const getItemStyles = useMemo(() => {
    return (item) => {
      const isActive =
        selectedId === item._id ||
        (!selectedId && item._id === selectedBrand?.value?._id);

      return {
        color: isActive ? '#fff' : 'inherit',
        backgroundColor: isActive ? 'rgb(38, 132, 255)' : 'initial',
        '&:hover': {
          backgroundColor: isActive
            ? 'rgb(38, 132, 255)'
            : 'rgb(222, 235, 255)',
        },
      };
    };
  }, [selectedId, selectedBrand?.value?._id]);

  // Optimized handle brand change
  const handleBrandChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSearchValue(value);
      const sanitizedValue = value.trim();
      debouncedSearch(sanitizedValue);
    },
    [debouncedSearch]
  );

  // Optimized display list with memoization
  const displayBrandList = useMemo(() => {
    let result = [];

    // Return filtered results if searching and have search value
    if (isSearch && searchValue.trim()) {
      // If we're loading, show previous search results or brandData to avoid empty list
      if (searchLoading) {
        // Show previous search results if available, otherwise show brandData
        result =
          filteredBrandList && filteredBrandList.length > 0
            ? filteredBrandList
            : brandData || [];
      } else {
        // Not loading - show search results (could be empty if no matches)
        result = filteredBrandList || [];
      }
    } else {
      // Return original data when not searching or no search value
      result = brandData || [];
    }

    // Filter out any invalid items to prevent empty list items
    const validResult = result.filter(
      (item) =>
        item &&
        typeof item === 'object' &&
        item.brandName &&
        typeof item.brandName === 'string' &&
        item.brandName.trim()
    );

    // Log only if there are filtered items to help debug
    if (result.length !== validResult.length) {
      console.warn('Filtered out invalid brand items:', {
        originalLength: result.length,
        validLength: validResult.length,
        filteredOut: result.length - validResult.length,
      });
    }

    return validResult;
  }, [isSearch, searchValue, filteredBrandList, brandData, searchLoading]);

  const resetSearch = () => {
    setIsSearch(false);
    setSearchValue('');
  };

  // Function to re-trigger search with updated active business filter
  const retriggerSearch = useCallback(() => {
    if (searchValue.trim()) {
      // Clear previous search results immediately when starting new search
      dispatch({
        type: 'RESET_SEARCH_BRAND_LIST',
        payload: null,
      });
      const isActive =
        localStorage.getItem('showActiveBusiness') === null
          ? true
          : Boolean(Number(localStorage.getItem('showActiveBusiness')));
      dispatch(searchBrandByLimit(10, 1, searchValue.trim(), false, isActive));
    }
  }, [dispatch, searchValue]);

  return {
    activeBrand,
    getItemStyles,
    handleBrandChange,
    searchValue,
    isSearch,
    displayBrandList,
    fetchMoreDataBySearch,
    paginationSearchBrand,
    searchLoading,
    resetSearch,
    retriggerSearch,
  };
};

export default useBrandList;
